import React, { useEffect, useRef, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";
import { useSettings } from "services/settings/SettingsContext";
import { getDateFnsLocale } from "internationalization/i18n";
import { MapEmployeeType } from "../SchedulingTypes";

const EmployeeTooltip: React.FC<{ employee: MapEmployeeType | null, x: number, y: number }> = ({ employee, x, y }) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const [pos, setPosition] = useState({ top: 0, left: 0 });
    const [ready, setReady] = useState(false);
    const tooltipRef = useRef<HTMLDivElement | null>(null);

    // Calculate the tooltip position, clamped to stay within the viewport
    useEffect(() => {
        if (!employee || !tooltipRef.current) return;

        const rect = tooltipRef.current.getBoundingClientRect();
        const viewWidth = window.innerWidth;
        const viewHeight = window.innerHeight;
        const offset = 12;

        let left = x + offset;
        let top = y + offset;

        if (left + rect.width > viewWidth) {
            left = x - rect.width - offset;
        }
        if (top + rect.height > viewHeight) {
            top = y - rect.height - offset;
        }
        if (left < offset) left = offset;
        if (top < offset) top = offset;

        setPosition({ top, left });
        setReady(true);
    }, [employee, x, y]);

    if (!employee) return null;

    // Map the employee status to a translated label
    const statusLabels: Record<string, string> = {
        traveling: t('scheduling.scheduler_map.employee_status_traveling_label'),
        working: t('scheduling.scheduler_map.employee_status_working_label'),
        unknown: t('scheduling.scheduler_map.employee_status_unknown_label'),
    };

    // Build the destination label from the job open in the app, street and city first and client name as fallback
    const streetPart = [employee.job_street, employee.job_house_number].filter(Boolean).join(' ');
    const destination = [streetPart, employee.job_city].filter(Boolean).join(', ') || employee.job_client_name || null;

    // Show where the employee is traveling to when the destination is known, otherwise a status line
    const statusLine = employee.status === 'traveling' && destination
        ? t('scheduling.scheduler_map.employee_traveling_to_label', { destination })
        : t('scheduling.scheduler_map.employee_status_line', { status: (statusLabels[employee.status] ?? employee.status).toLowerCase() });

    return (
        <div ref={tooltipRef}
             className="event-tooltip employee-tooltip"
             style={{
                position: "fixed",
                top: ready ? pos.top : -9999,
                left: ready ? pos.left : -9999,
                visibility: ready ? "visible" : "hidden",
            }}>
            <div className="tooltip-header">
                <h6>{employee.name}</h6>
                <div className="tooltip-time">
                    {formatDistanceToNow(new Date(employee.last_gps_at), { addSuffix: true, locale: getDateFnsLocale(userLocale) })}
                </div>
            </div>
            <div className="tooltip-content">
                {statusLine}
            </div>
        </div>
    );
};

export default EmployeeTooltip;
